import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import type { User } from 'src/types/user';
import { routePaths } from 'src/services/routing/routePaths';

import type { UserContextValue } from './index';

interface LocationState {
  originalLocation?: {
    pathname: string;
    search: string;
  };
}

export const useLocationState = () => {
  const location = useLocation<LocationState>();
  const searchParams = new URLSearchParams(location.search);

  return {
    location,
    searchParams,
  };
};

// Returns a function that redirects the user to the page they were trying to access
// before they were redirected to the login page.
// If there is no such page, return an empty function so the survey context can do its job
export const useHandleRedirectToOriginalLocation = () => {
  const history = useHistory();

  // user and switchSurvey have to be passed as params
  // since we call this function inside the userContext
  // and thus cannot access the context itself here.
  return useCallback(
    (
      user: User,
      originalLocation: LocationState['originalLocation'],
      switchSurvey: UserContextValue['switchSurvey'],
    ) => {
      const currentSurvey = user.session.survey;
      if (!originalLocation) return;

      const searchParams = new URLSearchParams(originalLocation.search);
      const surveyFromParams = searchParams.get('survey');

      // if no survey in params, redirect to either start or survey list.
      if (!surveyFromParams) {
        // if there is a current survey and the request isn't coming from the terms page,
        // redirect to start, else to survey list
        if (
          currentSurvey &&
          history.location.pathname !== routePaths.TERMS_OF_USAGE
        ) {
          history.push(routePaths.START);
        } else {
          history.push(routePaths.SURVEY_LIST);
        }
        return;
      }

      if (surveyFromParams === currentSurvey?.id.toString()) {
        history.push({
          pathname: originalLocation.pathname,
          search: originalLocation.search,
        });
      } else {
        switchSurvey(parseInt(surveyFromParams))
          .then(() => {
            history.push({
              pathname: originalLocation.pathname,
              search: originalLocation.search,
            });
          })
          .catch(() => {
            console.error(
              'user not allowed to switch to survey, redirecting to start',
            );
            history.push(routePaths.SURVEY_LIST);
          });
      }
    },
    [history],
  );
};
